<template>
  <div
    class="relative"
    v-bind:class="{ hovereffect: editable, 'rounded-full': rounded }"
    :style="{ height: height ? height + 'px' : '100%', width: width ? width + 'px' : '100%' }"
  >
    <img
      v-if="hasAvatar"
      v-bind:class="{ 'rounded-full': rounded }"
      class="block object-cover"
      :src="imgSrc"
      :style="{ height: height ? height + 'px' : '100%', width: width ? width + 'px' : '100%' }"
      @error="onAvatarError"
    />
    <img
      v-if="!hasAvatar && avatarType === 'user'"
      class="border object-cover"
      :class="{ 'rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-user.png"
    />
    <img
      v-if="!hasAvatar && avatarType === 'business'"
      class="border object-cover"
      :class="{ 'rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-business.png"
    />
    <img
      v-if="!hasAvatar && avatarType === 'plan'"
      class="border object-cover p-1 mx-auto my-auto"
      :class="{ 'rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-business-plan.png"
    />
    <img
      v-if="!hasAvatar && avatarType === 'integration'"
      class="border object-cover"
      :class="{ 'rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-integration.png"
    />
    <img
      v-if="!hasAvatar && avatarType === 'training'"
      class="border object-cover"
      :class="{ 'rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-training.png"
    />
    <div
      v-if="editable"
      class="opacity-0 hover:opacity-100 bg-gray-400 duration-300 left-0 top-0 h-full w-full absolute flex text-xs text-white font-light"
      @click="show"
      :class="{ 'rounded-full': rounded }"
    >
      <div class="flex flex-col w-full justify-center items-center">
        <span class="text-center">Upload</span>
        <upload-icon class="h-4 w-4 mt-1 text-white" />
      </div>
    </div>
    <base-modal v-if="editable" ref="avatarModal" size="sm">
      <base-card shadow>
        <div class="flex w-full justify-center">
          <image-input class="mt-6" v-model="imageFile" :key="imageInputKey" />
        </div>
        <div class="sm:flex sm:flex-row-reverse mt-6">
          <base-button @click="upload" :disabled="isLoading" :loading="isLoading" class="w-full sm:w-auto sm:ml-3">Upload</base-button>
          <base-button @click="hide" type="flat" class="w-full sm:w-auto mt-2 sm:mt-0"> Cancel </base-button>
        </div>
      </base-card>
    </base-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ImageInput from "@/components/ImageInput";
import { UploadIcon } from "@/assets/svgs";

export default {
  name: "Avatar",
  components: {
    ImageInput,
    UploadIcon,
  },
  props: {
    height: Number,
    width: Number,
    userId: {
      type: String,
      default: "",
    },
    businessId: {
      type: String,
      default: "",
    },
    planId: {
      type: String,
      default: "",
    },
    integrationId: {
      type: String,
      default: "",
    },
    trainingId: {
      type: String,
      default: "",
    },
    avatarType: {
      type: String,
      validator(value) {
        return ["user", "business", "plan", "integration", "training"].indexOf(value) !== -1;
      },
    },
    editable: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoading: false,
      avatarError: false,
      imageInputKey: 0,
      imageFile: null,
    };
  },
  computed: {
    ...mapGetters("avatar", ["getVersion"]),
    version() {
      return this.getVersion(this.id);
    },
    id() {
      return this.userId + this.businessId + this.planId + this.integrationId + this.trainingId;
    },
    hasAvatar() {
      return (this.userId || this.businessId || this.planId || this.integrationId || this.trainingId) && !this.avatarError;
    },
    imgSrc() {
      switch (this.avatarType) {
        case "user":
          return `/api/uploads/users/${this.userId}/avatar/base-image?v=${this.version}`;
        case "business":
          return `/api/uploads/businesses/${this.businessId}/avatar/base-image?v=${this.version}`;
        case "plan":
          return `/api/uploads/businesses/${this.businessId}/plans/${this.planId}/avatar/base-image?v=${this.version}`;
        case "integration":
          return `/api/uploads/integrations/${this.integrationId}/avatar/base-image?v=${this.version}`;
        case "training":
          return `/api/uploads/trainings/${this.trainingId}/avatar/base-image?v=${this.version}`;
        default:
          return "";
      }
    },
    imgUpload() {
      switch (this.avatarType) {
        case "user":
          return `/users/${this.userId}/upload/avatar`;
        case "business":
          return `/businesses/${this.businessId}/upload/avatar`;
        case "integration":
          return `/integrations/${this.integrationId}/avatar`;
        case "training":
          return `/businesses/${this.businessId}/trainings/${this.trainingId}/avatar`;
        case "plan":
          return `/businesses/${this.businessId}/plans/${this.planId}/upload/avatar`;
        default:
          return "";
      }
    },
  },
  methods: {
    ...mapActions("avatar", ["updateVersion"]),
    ...mapActions("businessActivation", { businessActivationInit: "init" }),
    reset() {
      this.imageInputKey++;
      this.isLoading = false;
      this.imageFile = null;
    },
    onAvatarError() {
      this.avatarError = true;
    },
    show() {
      this.reset();
      this.$refs.avatarModal.show();
    },
    hide() {
      this.$refs.avatarModal.hide();
    },
    upload() {
      this.isLoading = true;

      if (!this.imageFile) {
        this.isLoading = false;
        return;
      }

      var contentType = this.imageFile.type;

      this.$axios
        .post(this.imgUpload, { ContentType: contentType })
        .then((res) => {
          this.$axios
            .put(res.data, this.imageFile, { headers: { "Content-Type": contentType } })
            .then(() => {
              this.hide();
              this.updateVersion(this.id);
              this.avatarError = false;
              this.isLoading = false;
              this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Image Uploaded", message: "Your image was uploaded successfully" });
              if (this.avatarType === "business") this.businessActivationInit();
              this.$emit("image-uploaded");
            })
            .catch((err) => {
              this.isLoading = false;
              this.errors = err.response.data;
            });
        })
        .catch((err) => {
          this.isLoading = false;
          this.errors = err.response.data;
        });
    },
  },
  watch: {
    hasAvatar: {
      immediate: true,
      handler() {
        this.$emit("update:hasAvatar", this.hasAvatar);
      },
    },
  },
};
</script>
